import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { fetchAllGroups } from '../services/groupsService'
import type { Tenant } from '../types/tenant'
import { getAppPrefs, setAppPrefs } from '../utils/prefs'

import { useAuth } from './AuthContext'

type TenantContextType = {
  selectedTenant: Tenant | null
  setSelectedTenant: (tenant: Tenant) => void
  availableTenants: Tenant[]
  isLoading: boolean
  refreshTenants: () => Promise<void>
}

// Exported so test harnesses can supply a stub value without standing up the
// full TenantProvider (which depends on auth).
export const TenantContext = createContext<TenantContextType | undefined>(
  undefined,
)

export const useTenant = () => {
  const context = useContext(TenantContext)
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}

type TenantProviderProps = {
  children: ReactNode
}

const TENANT_STORAGE_KEY = 'selected_tenant'

export const TenantProvider = ({
  children,
}: TenantProviderProps): React.JSX.Element => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [selectedTenant, setSelectedTenantState] = useState<Tenant | null>(null)
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // Fetch tenants when user is loaded
  useEffect(() => {
    if (user) {
      refreshTenants()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Initialize selected tenant after tenants are loaded
  useEffect(() => {
    if (availableTenants.length === 0 || selectedTenant !== null) {
      return
    }

    try {
      // Get tenant from localStorage
      const savedTenant = getAppPrefs<Tenant>(TENANT_STORAGE_KEY)

      // If saved tenant exists in available tenants, use it
      if (savedTenant && availableTenants.some(t => t.id === savedTenant.id)) {
        setSelectedTenantState(savedTenant)
      } else {
        // Fall back to first available tenant
        setSelectedTenantState(availableTenants[0] || null)
      }
    } catch (error) {
      console.error('Failed to initialize tenant:', error)
      // Fall back to first available tenant
      setSelectedTenantState(availableTenants[0] || null)
    }
  }, [availableTenants, selectedTenant])

  const setSelectedTenant = (tenant: Tenant) => {
    setSelectedTenantState(tenant)
    setAppPrefs(tenant, TENANT_STORAGE_KEY)

    try {
      // Store current URL parameters before invalidating queries
      const currentParams = new URLSearchParams(window.location.search)
      const preservedParams = {
        ids: currentParams.get('ids'),
        item: currentParams.get('item'),
        affectedResourceId: currentParams.get('affectedResourceId'),
        incident_id: currentParams.get('incident_id'),
        status: currentParams.get('status'),
        businessImpact: currentParams.get('businessImpact'),
        severity: currentParams.get('severity'),
        provider: currentParams.get('provider'),
        accountId: currentParams.get('accountId'),
        detectionMethod: currentParams.get('detectionMethod'),
        type: currentParams.get('type'),
        title: currentParams.get('title'),
      }

      // Warn if both item and ids parameters exist (potential conflict)
      if (preservedParams.item && preservedParams.ids) {
        console.warn(
          'TenantContext: Both item and ids parameters detected in URL',
          {
            item: preservedParams.item,
            ids: preservedParams.ids,
            resolution: 'item parameter will take precedence',
          },
        )
      }

      // Invalidate all queries to trigger refetch with new tenant context
      queryClient.invalidateQueries()

      // Wait for query invalidation to complete rather than using fixed delay
      // Use requestIdleCallback for better timing, fallback to setTimeout
      const applyUrlChanges = () => {
        try {
          const newParams = new URLSearchParams(window.location.search)

          // Update tenant parameter
          newParams.set('tenant', tenant.id)

          // Re-apply preserved parameters (keep item as item, skip any ids)
          Object.entries(preservedParams).forEach(([key, value]) => {
            if (value && key !== 'ids') {
              // Skip 'ids' - we want to preserve 'item' for URL redirect flow
              newParams.set(key, value)
            }
          })

          // Create processed params for the event (convert item -> ids for EventsFilterContext)
          const processedParams = { ...preservedParams }
          if (preservedParams.item && !preservedParams.ids) {
            processedParams.ids = preservedParams.item
          }

          // Update URL without triggering navigation
          const newUrl = window.location.pathname + '?' + newParams.toString()
          window.history.replaceState({}, '', newUrl)

          // Dispatch a custom event to notify components to re-read URL parameters
          window.dispatchEvent(
            new CustomEvent('tenant-change-complete', {
              detail: { tenantId: tenant.id, preservedParams: processedParams },
            }),
          )
        } catch (error) {
          console.error(
            'TenantContext: Failed to apply URL changes after tenant switch',
            {
              tenantId: tenant.id,
              error: error instanceof Error ? error.message : 'Unknown error',
              preservedParams,
            },
          )
        }
      }

      // Use requestIdleCallback for better timing coordination, with setTimeout fallback
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(applyUrlChanges, { timeout: 300 })
      } else {
        setTimeout(applyUrlChanges, 150) // Fallback for environments without requestIdleCallback
      }
    } catch (error) {
      console.error('TenantContext: Failed to initiate tenant change', {
        tenantId: tenant.id,
        error: error instanceof Error ? error.message : 'Unknown error',
      })

      // Still try to set the tenant state even if URL handling fails
      // The tenant switch should work even if permalinks don't
    }
  }

  const refreshTenants = async (): Promise<void> => {
    try {
      setIsLoading(true)

      // Check if user is super_admin
      const isSuperAdmin = user?.groups?.some(
        group => group.metadata.role === 'super_admin',
      )

      let tenants: Tenant[] = []

      if (isSuperAdmin) {
        // Super admins: Fetch all tenants via global API
        const response = await fetchAllGroups({
          limit: 250,
          group_type: 'organization',
        })

        // Transform GroupWithTenant[] to Tenant[]
        tenants = response.items.map(group => ({
          id: group.tenant_name, // tenant slug
          name: group.name, // organization/group name (display name)
          domain:
            (typeof group.metadata.domain === 'string'
              ? group.metadata.domain
              : '') || '', // optional domain
          subscription:
            (group.metadata.subscription &&
            typeof group.metadata.subscription === 'object' &&
            group.metadata.subscription !== null &&
            'plan' in group.metadata.subscription
              ? (group.metadata.subscription as { plan: string }).plan
              : 'Unknown') || 'Unknown',
        }))
      } else {
        // Build tenants array using tenant_names (actual tenant identifiers)
        tenants = (user?.groups || []).map(group => ({
          id: group.group_slug,
          name: group.group_name,
          domain: '',
          subscription: 'Unknown',
        }))
      }

      setAvailableTenants(tenants)
    } catch (error) {
      console.error('Failed to refresh tenants:', error)
      // Set empty array on error
      setAvailableTenants([])
    } finally {
      setIsLoading(false)
    }
  }

  const contextValue: TenantContextType = {
    selectedTenant,
    setSelectedTenant,
    availableTenants,
    isLoading,
    refreshTenants,
  }

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  )
}
